import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Heart, Sparkles, User, LogOut, Menu, X, Settings, Package, ShoppingBag, Image as ImageIcon, DollarSign, Receipt, BarChart3, Upload, Building2, Layers, Calculator, FileText, LayoutDashboard, HelpCircle, Shield, Users as UsersIcon, Store } from 'lucide-react';
import { useState, useEffect } from 'react';
import { Toaster } from './ui/sonner';
import { adminAPI } from '../lib/api';

export const Layout = ({ children }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);

  // Check if user is admin
  useEffect(() => {
    const checkAdminStatus = async () => {
      if (!user) {
        setIsAdmin(false);
        return;
      }
      
      try {
        // Try to access admin stats - if successful, user is admin
        await adminAPI.getStats();
        setIsAdmin(true);
      } catch (error) {
        setIsAdmin(false);
      }
    };
    
    checkAdminStatus();
  }, [user]);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const navLinks = [
    { href: '/events', label: 'My Events', icon: Heart },
    { href: '/ai-discover', label: 'Event Scout', icon: Sparkles },
    { href: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { href: '/community', label: 'Vendor Community', icon: UsersIcon },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="sticky top-0 z-50 glass-header border-b-2 border-[#1E3A5F]">
        <div className="px-6 md:px-12 max-w-7xl mx-auto">
          <div className="flex items-center justify-between h-16 md:h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-2 group" data-testid="logo-link">
              <img 
                src="/ams_compass_logo.png" 
                alt="AMSCompass" 
                className="h-24 w-auto"
              />
              <div className="hidden sm:block">
                <span className="text-xl md:text-2xl font-bold font-['Outfit'] text-[#000501]">AMS</span>
                <span className="text-xl md:text-2xl font-bold font-['Outfit'] text-[#1E3A5F]">Compass</span>
              </div>
            </Link>

            {/* Desktop Nav */}
            <nav className="hidden md:flex items-center gap-2">
              {navLinks.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  data-testid={`nav-${link.href.slice(1)}`}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
                    isActive(link.href)
                      ? 'bg-[#1E3A5F] text-white'
                      : 'text-[#000501] hover:bg-[#A5CBC3]/30'
                  }`}
                >
                  <link.icon className="w-4 h-4" />
                  {link.label}
                </Link>
              ))}
            </nav>

            {/* User Menu / Auth Buttons */}
            <div className="flex items-center gap-3">
              {user ? (
                <>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="outline"
                        data-testid="user-menu-trigger"
                        className="border-2 border-[#1E3A5F] rounded-full px-4 py-2 font-medium shadow-hard hover:shadow-hard-hover hover:-translate-y-0.5 transition-transform text-[#000501]"
                      >
                        <User className="w-4 h-4 mr-2" />
                        {user.name?.split(' ')[0]}
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-48 border-2 border-[#1E3A5F] rounded-xl shadow-hard">
                      <DropdownMenuItem asChild>
                        <Link to="/products" className="flex items-center gap-2" data-testid="menu-products">
                          <ShoppingBag className="w-4 h-4" />
                          Products
                        </Link>
                      </DropdownMenuItem>
                      <DropdownMenuItem asChild>
                        <Link to="/materials" className="flex items-center gap-2" data-testid="menu-materials">
                          <Package className="w-4 h-4" />
                          Materials
                        </Link>
                      </DropdownMenuItem>
                      <DropdownMenuItem asChild>
                        <Link to="/sales" className="flex items-center gap-2" data-testid="menu-sales">
                          <DollarSign className="w-4 h-4" />
                          Sales
                        </Link>
                      </DropdownMenuItem>
                      <DropdownMenuItem asChild>
                        <Link to="/boutiques" className="flex items-center gap-2" data-testid="menu-boutiques">
                          <Building2 className="w-4 h-4" />
                          Boutiques
                        </Link>
                      </DropdownMenuItem>
                      <DropdownMenuItem asChild>
                        <Link to="/inventory" className="flex items-center gap-2" data-testid="menu-inventory">
                          <Layers className="w-4 h-4" />
                          Inventory
                        </Link>
                      </DropdownMenuItem>
                      <DropdownMenuItem asChild>
                        <Link to="/expenses" className="flex items-center gap-2" data-testid="menu-expenses">
                          <Receipt className="w-4 h-4" />
                          Expenses
                        </Link>
                      </DropdownMenuItem>
                      <DropdownMenuItem asChild>
                        <Link to="/media" className="flex items-center gap-2" data-testid="menu-media">
                          <ImageIcon className="w-4 h-4" />
                          Media Library
                        </Link>
                      </DropdownMenuItem>
                      <DropdownMenuItem asChild>
                        <Link to="/analytics" className="flex items-center gap-2" data-testid="menu-analytics">
                          <BarChart3 className="w-4 h-4" />
                          Analytics
                        </Link>
                      </DropdownMenuItem>
                      <DropdownMenuItem asChild>
                        <Link to="/what-if" className="flex items-center gap-2" data-testid="menu-what-if">
                          <Calculator className="w-4 h-4" />
                          What-If Planner
                        </Link>
                      </DropdownMenuItem>
                      <DropdownMenuItem asChild>
                        <Link to="/applications" className="flex items-center gap-2" data-testid="menu-applications">
                          <FileText className="w-4 h-4" />
                          Applications
                        </Link>
                      </DropdownMenuItem>
                      {isAdmin && (
                        <DropdownMenuItem asChild>
                          <Link to="/import" className="flex items-center gap-2" data-testid="menu-import">
                            <Upload className="w-4 h-4" />
                            Bulk Import
                          </Link>
                        </DropdownMenuItem>
                      )}
                      <DropdownMenuSeparator />
                      <DropdownMenuItem asChild>
                        <Link to="/help" className="flex items-center gap-2" data-testid="menu-help">
                          <HelpCircle className="w-4 h-4" />
                          Help & Tutorials
                        </Link>
                      </DropdownMenuItem>
                      <DropdownMenuItem asChild>
                        <Link to="/profile/edit" className="flex items-center gap-2" data-testid="menu-edit-vendor-profile">
                          <Store className="w-4 h-4" />
                          Edit Vendor Profile
                        </Link>
                      </DropdownMenuItem>
                      <DropdownMenuItem asChild>
                        <Link to="/settings" className="flex items-center gap-2" data-testid="menu-settings">
                          <Settings className="w-4 h-4" />
                          Settings
                        </Link>
                      </DropdownMenuItem>
                      {isAdmin && (
                        <DropdownMenuItem asChild>
                          <Link to="/admin" className="flex items-center gap-2 text-[#1E3A5F] font-medium" data-testid="menu-admin">
                            <Shield className="w-4 h-4" />
                            Admin Dashboard
                          </Link>
                        </DropdownMenuItem>
                      )}
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={handleLogout} className="text-red-600" data-testid="menu-logout">
                        <LogOut className="w-4 h-4 mr-2" />
                        Logout
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </>
              ) : (
                <div className="hidden md:flex items-center gap-3">
                  <Link to="/login">
                    <Button variant="ghost" className="font-medium text-[#000501]" data-testid="nav-login">
                      Log in
                    </Button>
                  </Link>
                  <Link to="/signup">
                    <Button className="btn-primary text-sm px-6 py-2" data-testid="nav-signup">
                      Sign up
                    </Button>
                  </Link>
                </div>
              )}

              {/* Mobile menu button */}
              <button
                className="md:hidden p-2 rounded-lg hover:bg-[#A5CBC3]/30"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                data-testid="mobile-menu-toggle"
              >
                {mobileMenuOpen ? <X className="w-6 h-6 text-[#000501]" /> : <Menu className="w-6 h-6 text-[#000501]" />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden border-t-2 border-[#1E3A5F] bg-white px-6 py-4">
            <nav className="flex flex-col gap-2">
              {navLinks.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  onClick={() => setMobileMenuOpen(false)}
                  data-testid={`mobile-nav-${link.href.slice(1)}`}
                  className={`flex items-center gap-3 px-4 py-3 rounded-lg font-medium transition-colors ${
                    isActive(link.href) ? 'bg-[#1E3A5F] text-white' : 'text-[#000501] hover:bg-[#A5CBC3]/30'
                  }`}
                >
                  <link.icon className="w-5 h-5" />
                  {link.label}
                </Link>
              ))}
              {user ? (
                <>
                  <button
                    onClick={() => { handleLogout(); setMobileMenuOpen(false); }}
                    className="flex items-center gap-3 px-4 py-3 rounded-lg font-medium text-[#FE5F55] hover:bg-red-50"
                  >
                    <LogOut className="w-5 h-5" />
                    Logout
                  </button>
                </>
              ) : (
                <div className="flex flex-col gap-2 pt-2 border-t border-[#A5CBC3]">
                  <Link to="/login" onClick={() => setMobileMenuOpen(false)}>
                    <Button variant="outline" className="w-full border-2 border-[#1E3A5F] text-[#000501]">Log in</Button>
                  </Link>
                  <Link to="/signup" onClick={() => setMobileMenuOpen(false)}>
                    <Button className="btn-primary w-full">Sign up</Button>
                  </Link>
                </div>
              )}
            </nav>
          </div>
        )}
      </header>

      {/* Main Content */}
      <main>{children}</main>

      {/* Toast notifications */}
      <Toaster position="top-right" richColors />
    </div>
  );
};

export default Layout;
